import { ContainerIcon } from 'lucide-react';
import { useMemo } from 'react';

import { AsnAction, AsnStatus, type AsnLineDTO } from '@invenco/supply-interface';

import { DetailGroup } from '@/components/detail-group';
import { Header, HeaderIcon, HeaderTitle } from '@/components/header';
import { DetailMain, DetailSide, Page, PageContent } from '@/components/layout';
import { StatusBadge } from '@/components/status-badge';
import { useRequiredParam } from '@/shared/hooks/use-required-param';
import { asnStatusType } from '@/shared/status-maps';
import { asnStatusTitle } from '@/shared/title-maps';

import { AsnLinesCard } from '../asn-lines-card/asn-lines-card';
import type { AsnLine } from '../asn-lines-card/asn-lines-card-columns';
import { EditableAsnLinesCard } from '../asn-lines-card/editable-asn-lines-card';
import { AsnActions } from './asn-actions';
import { AsnDetailsSection } from './asn-details-section';
import { useAsn } from './use-asn';

const getAsnLineFromDto = (line: AsnLineDTO): AsnLine => ({
  id: line.id,
  skuId: line.skuId,
  skuName: line.skuName,
  description: line.description,
  qtyExpected: line.qtyExpected,
  qtyReceived: line.qtyReceived,
  unitPurchaseCost: line.unitPurchaseCost,
  unitCostTotal: line.unitCostTotal,
});

export function AsnDetails() {
  const id = useRequiredParam('id');
  const {
    asn,
    isLoading,
    refetchAsn,
    updateAsn,
    bookAsn,
    cancelAsn,
    addAsnLine,
    updateAsnLine,
    recostAsnLine,
  } = useAsn(id);

  const canEdit = Boolean(asn?.allowableActions?.includes(AsnAction.EDIT));
  const canRecost = Boolean(asn?.allowableActions?.includes(AsnAction.RECOST));
  const showQtyReceived = asn?.status === AsnStatus.CANCELLED || asn?.status === AsnStatus.CLOSED;
  const lines = useMemo(
    () => (asn?.asnLines ?? []).map((line) => getAsnLineFromDto(line)),
    [asn?.asnLines],
  );

  return (
    <Page>
      <Header loading={isLoading}>
        <HeaderIcon parentUrl="/supply/asns" parentTitle="ASNs">
          <ContainerIcon />
        </HeaderIcon>
        <HeaderTitle pageTitle={asn?.name} className="flex flex-wrap items-center gap-2">
          {asn?.name}
          {asn?.status ? (
            <StatusBadge status={asn.status} type={asnStatusType} title={asnStatusTitle} />
          ) : null}
        </HeaderTitle>
        <AsnActions
          asn={asn}
          isLoading={isLoading}
          onRefresh={refetchAsn}
          updateAsn={updateAsn}
          bookAsn={bookAsn}
          cancelAsn={cancelAsn}
        />
      </Header>
      <PageContent variant="split">
        <DetailMain>
          {canEdit || canRecost ? (
            <EditableAsnLinesCard
              title="Items"
              lines={lines}
              isLoading={isLoading}
              showQtyReceived={showQtyReceived}
              isRecosting={canRecost}
              onAddLine={canRecost ? undefined : addAsnLine}
              onUpdateLine={canRecost ? recostAsnLine : updateAsnLine}
            />
          ) : (
            <AsnLinesCard
              title="Items"
              lines={lines}
              isLoading={isLoading}
              showQtyReceived={showQtyReceived}
            />
          )}
        </DetailMain>
        <DetailSide>
          <DetailGroup>
            <AsnDetailsSection asn={asn} isLoading={isLoading} />
          </DetailGroup>
        </DetailSide>
      </PageContent>
    </Page>
  );
}
